import {PropertyFilter} from 'common/widgets/property-filter-menu/models/property-filter';
import {NumberOperator} from 'common/widgets/property-filter-menu/models/types';
import {FiltersOperator, QueryBehaviorEntry, SelectorTypeDefinitions} from 'common/widgets/query-entry-section/types';
import {generateDateRangeTrackingProps} from 'common/widgets/date-range-picker-new/util';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {commonTrackingEventPropertyName} from 'common/report/tracking-setup';
import {getCurrentMoment} from 'common/util/date';
import {isFirstTimeEventFilter} from 'common/widgets/property-filter-menu/models/utils';
import {PRIMATIVE_MATH_TYPES, PropertyType, ResourceType} from 'common/report/constants';
import {DateRangeType, TimeUnit} from 'common/widgets/date-range-picker/models/types';

export const DEFAULT_EMPTY_BEHAVIORAL_ENTRY: QueryBehaviorEntry = {
  aggregationOperator: PRIMATIVE_MATH_TYPES.TOTAL,
  aggregationOperatorPerUser: null,
  event: null,
  filters: [],
  filtersOperator: FiltersOperator.And,
  behavioralFiltersOperator: FiltersOperator.And,
  property: null,
  type: SelectorTypeDefinitions.Behavior,
  dateRange: {
    type: DateRangeType.RelativeAfter,
    window: {
      unit: TimeUnit.Day,
      value: 30,
    },
  },
  entryFilter: {
    customProperty: null,
    customPropertyId: null,
    resourceType: ResourceType.Event,
    propertyObjectKey: null,
    propertyDefaultType: PropertyType.Number,
    propertyType: PropertyType.Number,
    filterOperator: NumberOperator.AtLeast,
    filterValue: 1,
  },
};

/**
 * Transforms behavioral frequency filters to event based entries
 */
export function transformBehavioralFilterToBehavioralEntry(
  filter: PropertyFilter,
  behavioralFiltersOperator?: FiltersOperator,
): QueryBehaviorEntry {
  const {behavior} = filter.customProperty;

  return {
    // Default filters
    filters: [],
    ...behavior,
    behavioralFiltersOperator: behavioralFiltersOperator ?? FiltersOperator.And,
    filtersOperator: behavior?.filtersOperator || FiltersOperator.And,
    type: ResourceType.Event,
    entryFilter: {
      ...filter,
      filterDateUnit: behavior.property?.unit,
      // an entry filter defines no property
      customProperty: null,
      customPropertyId: null,
      // entry filters act on ResourceType.Event, while the behavioral prop acts on ResourceType.User
      resourceType: ResourceType.Event,
    },
  };
}

/**
 * Defines tracking props for a behavior entry (usually a behavioral filter)
 * @param entry
 */
export function getBehaviorTrackingProps(entry: QueryBehaviorEntry) {
  const hasPropertyAggregation = Boolean(
    entry.property?.customProperty || entry.property?.customPropertyId || entry.property?.value,
  );
  const aggregator = hasPropertyAggregation ? `property_${entry.aggregationOperator}` : entry.aggregationOperator;

  return {
    // captures behavior date range trackingd
    ...generateDateRangeTrackingProps(
      new DateRange(entry.dateRange),
      null,
      null,
      getCurrentMoment(),
      `cohorts-query-builder`,
    ),
    // captures entry filter tracking
    ...(entry.entryFilter
      ? {
          'Filter operator': entry.entryFilter.filterOperator,
          'Filter value': entry.entryFilter.filterValue,
          'Filter type': entry.entryFilter.propertyType,
        }
      : {}),
    'Filters operator': entry.filtersOperator,
    '# of filters': entry.filters.length,
    'has 1st time filter': entry.filters.some((filter) => isFirstTimeEventFilter(filter)),
    // in a behavioral context, all operators are per-user
    Aggregator: aggregator,
    // captures property aggregations
    Property: entry.property?.value ?? null,
    'Property unit': entry.property?.unit ?? null,
    'Property data type': entry.property?.type ?? null,
    'Property default type': entry.property?.propertyDefaultType ?? null,

    'Event name': entry.event.label ?? entry.event.value,
    'is custom event': entry.event.custom,
    [commonTrackingEventPropertyName.usedUserCustomProperty]:
      (entry.property?.customPropertyId || entry.property?.customProperty) ?? false,
  };
}

export function isBehavioralFilterNegated(entry: QueryBehaviorEntry) {
  return (
    entry.property === null &&
    entry.aggregationOperator === PRIMATIVE_MATH_TYPES.TOTAL &&
    entry.entryFilter?.filterOperator === NumberOperator.EqualTo &&
    entry.entryFilter?.filterValue === 0
  );
}
